import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { getJwtRoleSlug } from "../../../public/utils/jwtRole";
import { getVisitors } from "../api/visitorsApi";
import { resolveVisitorStatus } from "./visitorStatus";
import { VISITOR_PAGE_SIZE } from "./visitorListConstants";
import { filterVisitorsForLoggedInUser } from "./visitorListScope";

function matchesSearch(row, q) {
  if (!q) return true;
  const needle = q.toLowerCase();
  const hay = [
    row.visitor_code,
    row.visitor_name,
    row.mobile_number,
    row.purpose_name,
    row.person_to_meet_name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
}

export function useVisitorList({ pageSize = VISITOR_PAGE_SIZE, listScope = "self" } = {}) {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const debouncedSetSearch = useDebouncedCallback((v) => {
    setDebouncedSearch(v);
    setPage(1);
  }, 300);

  const handleSearchChange = (v) => {
    setSearch(v);
    debouncedSetSearch(v.trim());
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getVisitors({ scope: listScope });
      const roleSlug =
        typeof localStorage !== "undefined"
          ? getJwtRoleSlug(localStorage.getItem("jwt_token"))
          : "";
      const rows = filterVisitorsForLoggedInUser(res?.data, roleSlug, listScope);
      setAllRows(rows);
    } catch (err) {
      setError(err?.message || "Failed to load visitors");
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [listScope]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    return allRows.filter((row) => {
      if (statusFilter && resolveVisitorStatus(row) !== statusFilter) return false;
      return matchesSearch(row, debouncedSearch);
    });
  }, [allRows, debouncedSearch, statusFilter]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const pageRows = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize, totalPages]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return {
    rows: pageRows,
    allRows,
    loading,
    error,
    search,
    statusFilter,
    setStatusFilter: (v) => {
      setStatusFilter(v);
      setPage(1);
    },
    handleSearchChange,
    page,
    setPage,
    pageSize,
    total,
    totalPages,
    refresh,
  };
}
